import React, {useState, useMemo} from "react";
import {Link, useNavigate, useLocation} from "react-router-dom";
import {useHasTicker} from "../../Components/TickerBar";
import {message} from "antd";
import {useDispatch} from "react-redux";
import {useGetGovernorateQuery, useRegisterMutation} from "../../RTK/Slices/Slices/apiSliceMeat";
import {reduxLogin, saveUserData} from "../../RTK/Slices/authSlice";
import {language} from "../../language/Language";
import {Helmet} from "react-helmet";

const texts = {
    ar: {
        title: "تسجيل البيانات",
        name: "الاسم",
        namePlaceholder: "أدخل اسمك بالكامل هنا",
        phone: "رقم الهاتف",
        phonePlaceholder: "أدخل رقم هاتفك (مثال: 01xxxxxxxx)",
        deliveryArea: "اختر منطقة التوصيل",
        deliveryAreaPlaceholder: "اختر منطقتك من القائمة",
        street: "اسم الشارع (اختياري)",
        streetPlaceholder: "أدخل اسم الشارع (اختياري)",
        building: "رقم المبنى",
        buildingPlaceholder: "",
        apartment: "رقم الشقة",
        floor: "رقم الدور",
        password: "إنشاء كلمة مرور (اختياري)",
        passwordPlaceholder: "أدخل كلمة مرور",
        passwordHint: "يُرجى استخدام أرقام فقط لكلمة المرور",
        confirm: "تأكيد",
        cancel: "إلغاء",
        confirming: "جاري التسجيل...",
        fillRequiredFields: "يرجى ملء جميع الحقول المطلوبة.",
        registrationSuccess: "تم التسجيل بنجاح! سيتم تحويلك...",
        registrationFailed: "فشل التسجيل. يرجى المحاولة مرة أخرى.",
        passwordLength: "يجب أن تكون كلمة المرور 8 أرقام على الأقل.",
        passwordOnlyNumbers: "يجب أن تحتوي كلمة المرور على أرقام فقط.",
        phoneRegistered: "عفوا هذا الرقم تم تسجيله",
        emailRegistered: "عفوا البريد الإلكتروني مستخدم بالفعل",
        generalError: "عفوا لقد حدث شيء خاطئ. برجاء المحاولة لاحقا",
    },
    en: {
        title: "Register",
        name: "Name",
        namePlaceholder: "Enter your full name",
        phone: "Phone Number",
        phonePlaceholder: "Enter your phone (e.g. 01xxxxxxxx)",
        deliveryArea: "Choose Delivery Area",
        deliveryAreaPlaceholder: "Select your area from the list",
        street: "Street Name (Optional)",
        streetPlaceholder: "Enter street name (optional)",
        building: "Building Number",
        buildingPlaceholder: "",
        apartment: "Apartment Number",
        floor: "Floor Number",
        password: "Create Password (Optional)",
        passwordPlaceholder: "Enter password",
        passwordHint: "Please use numbers only for the password",
        confirm: "Confirm",
        cancel: "Cancel",
        confirming: "Registering...",
        fillRequiredFields: "Please fill in all required fields.",
        registrationSuccess: "Registration successful! Redirecting...",
        registrationFailed: "Registration failed. Please try again.",
        passwordLength: "Password must be at least 8 digits long.",
        passwordOnlyNumbers: "Password must contain only numbers.",
        phoneRegistered: "Sorry, this phone number is already registered",
        emailRegistered: "Sorry, this email is already in use",
        generalError: "Sorry, something went wrong. Please try again later",
    },
};

const inputStyle = {
    width: "100%",
    padding: "14px 16px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    fontSize: "15px",
    color: "#333",
    backgroundColor: "#fff",
    boxSizing: "border-box",
    outline: "none",
    direction: "rtl",
    textAlign: "right",
    fontFamily: "Cairo, sans-serif",
};

const labelStyle = {
    display: "block",
    textAlign: "right",
    color: "#222",
    fontSize: "15px",
    fontWeight: "bold",
    marginBottom: "6px",
};

const Registration = () => {
    const hasTicker = useHasTicker();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || "/";
    const dispatch = useDispatch();
    const [messageApi, contextHolder] = message.useMessage();
    const t = texts[language === "en" ? "en" : "ar"];

    const {data: governorateData, isLoading: isLoadingGovernorate} = useGetGovernorateQuery();
    const [newUser, {isLoading}] = useRegisterMutation();

    const [createUser, setCreateUser] = useState({
        name: "",
        email: "",
        mobile: "",
        governorate: "",
        city: "",   // building number
        street: "", // floor
        building: "", // apartment
        password: "",
        address: "",
    });

    const [showPassword, setShowPassword] = useState(false);
    const [isGovPopupOpen, setIsGovPopupOpen] = useState(false);
    const [govSearchTerm, setGovSearchTerm] = useState("");
    const [selectedGovName, setSelectedGovName] = useState("");

    const activeGovernorates = useMemo(() => {
        return governorateData?.data?.filter((gov) => gov.status === 1) || [];
    }, [governorateData]);

    const handleGovernorateChange = (value) => {
        setCreateUser((prev) => ({...prev, governorate: value}));
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        if (name === "password") {
            const numbersOnly = value.replace(/\D/g, "");
            setCreateUser((prev) => ({...prev, [name]: numbersOnly}));
        } else {
            setCreateUser((prev) => ({...prev, [name]: value}));
        }
    };

    const validatePassword = (password) => {
        if (!password) return null; // optional
        if (password.length < 8) return t.passwordLength;
        if (!/^\d+$/.test(password)) return t.passwordOnlyNumbers;
        return null;
    };

    const validateRequiredFields = () => {
        return createUser.name.trim() !== "" &&
            createUser.mobile.trim() !== "" &&
            createUser.governorate !== "" &&
            createUser.city.trim() !== "" &&
            createUser.street.trim() !== "" &&
            createUser.building.trim() !== "";
    };

    const showMessage = (text, type = "info") => {
        if (type === "success") messageApi.success(text);
        else if (type === "error") messageApi.error(text);
        else messageApi.info(text);
    };

    const handleApiError = (response) => {
        if (response?.success === 10) {
            showMessage(response.msg || t.phoneRegistered, "error");
            return true;
        }
        if (response?.success === 0) {
            if (response?.error?.includes("users_username_unique")) {
                showMessage(t.emailRegistered, "error");
                return true;
            }
            showMessage(response.msg || t.generalError, "error");
            return true;
        }
        return false;
    };

    const handleRegistration = async (e) => {
        e.preventDefault();
        if (!validateRequiredFields()) {
            showMessage(t.fillRequiredFields, "error");
            return;
        }
        const pwErr = validatePassword(createUser.password);
        if (pwErr) {
            showMessage(pwErr, "error");
            return;
        }
        try {
            const response = await newUser(createUser).unwrap();
            if (handleApiError(response)) return;
            if (response?.success === 1) {
                const user = response?.user;
                localStorage.setItem("user", JSON.stringify(user));
                dispatch(reduxLogin());
                dispatch(saveUserData(user));
                showMessage(response.msg || t.registrationSuccess, "success");
                setTimeout(() => navigate(from, {replace: true}), 2000);
            } else {
                showMessage(t.registrationFailed, "error");
            }
        } catch (err) {
            console.error("Registration failed:", err);
            if (err?.data) {
                if (handleApiError(err.data)) return;
                if (err.data.data) {
                    Object.entries(err.data.data).forEach(([field, messages]) => {
                        showMessage(`${field}: ${messages.join(", ")}`, "error");
                    });
                } else {
                    showMessage(err.data.msg || t.registrationFailed, "error");
                }
            } else {
                showMessage(t.registrationFailed, "error");
            }
        }
    };

    return (
        <div
            dir="rtl"
            style={{
                minHeight: "100vh",
                background: "#f0eae4",
                fontFamily: "Cairo, sans-serif",
                padding: `${hasTicker ? 152 : 110}px 0 40px 0`,
                transition: "padding-top 0.3s",
            }}
        >
            <Helmet>
                <title>Meat-House El-Fardos - تسجيل البيانات</title>
                <meta name="description" content="تسجيل حساب جديد في Meat House" />
            </Helmet>
            {contextHolder}

            <div
                style={{
                    maxWidth: "480px",
                    margin: "0 auto",
                    padding: "30px 20px",
                }}
            >
                {/* Title */}
                <h1
                    style={{
                        textAlign: "center",
                        color: "#b22222",
                        fontSize: "28px",
                        fontWeight: "bold",
                        marginBottom: "28px",
                    }}
                >
                    {t.title}
                </h1>

                <form onSubmit={handleRegistration}>

                    {/* Name */}
                    <div style={{marginBottom: "16px"}}>
                        <label style={labelStyle}>
                            {t.name} <span style={{color: "#b22222"}}>*</span>
                        </label>
                        <input
                            name="name"
                            type="text"
                            value={createUser.name}
                            onChange={handleInputChange}
                            placeholder={t.namePlaceholder}
                            required
                            style={inputStyle}
                        />
                    </div>

                    {/* Phone */}
                    <div style={{marginBottom: "16px"}}>
                        <label style={labelStyle}>
                            {t.phone} <span style={{color: "#b22222"}}>*</span>
                        </label>
                        <input
                            name="mobile-no-autofill"
                            type="tel"
                            autoComplete="one-time-code"
                            data-lpignore="true"
                            data-1p-ignore="true"
                            value={createUser.mobile}
                            onChange={(e) => setCreateUser((prev) => ({...prev, mobile: e.target.value}))}
                            placeholder={t.phonePlaceholder}
                            required
                            style={inputStyle}
                        />
                    </div>

                    {/* Delivery Area */}
                    <div style={{marginBottom: "16px"}}>
                        <label style={labelStyle}>
                            {t.deliveryArea} <span style={{color: "#b22222"}}>*</span>
                        </label>
                        <button
                            type="button"
                            onClick={() => { setIsGovPopupOpen(true); setGovSearchTerm(""); }}
                            style={{
                                ...inputStyle,
                                textAlign: "right",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: "pointer",
                                background: "#fff",
                                color: selectedGovName ? "#222" : "#aaa",
                            }}
                        >
                            <span>{selectedGovName || t.deliveryAreaPlaceholder}</span>
                            <span style={{color: "#666", fontSize: "14px"}}>▼</span>
                        </button>
                    </div>

                    {/* Street (optional) */}
                    <div style={{marginBottom: "16px"}}>
                        <label style={{...labelStyle, color: "#555", fontWeight: "600"}}>
                            {t.street}
                        </label>
                        <input
                            name="street-name-no-autofill"
                            type="text"
                            autoComplete="one-time-code"
                            data-lpignore="true"
                            data-1p-ignore="true"
                            value={createUser.address}
                            onChange={(e) => setCreateUser((prev) => ({...prev, address: e.target.value}))}
                            placeholder={t.streetPlaceholder}
                            style={inputStyle}
                        />
                    </div>

                    {/* Building Number */}
                    <div style={{marginBottom: "16px"}}>
                        <label style={labelStyle}>
                            {t.building} <span style={{color: "#b22222"}}>*</span>
                        </label>
                        <input
                            name="building-no-autofill"
                            type="text"
                            autoComplete="one-time-code"
                            data-lpignore="true"
                            data-1p-ignore="true"
                            value={createUser.city}
                            onChange={(e) => setCreateUser((prev) => ({...prev, city: e.target.value}))}
                            placeholder=""
                            required
                            style={inputStyle}
                        />
                    </div>

                    {/* Apartment + Floor in two columns */}
                    <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px"}}>
                        {/* Apartment */}
                        <div>
                            <label style={labelStyle}>
                                {t.apartment} <span style={{color: "#b22222"}}>*</span>
                            </label>
                            <input
                                name="apartment-no-autofill"
                                type="text"
                                autoComplete="one-time-code"
                                data-lpignore="true"
                                data-1p-ignore="true"
                                value={createUser.building}
                                onChange={(e) => setCreateUser((prev) => ({...prev, building: e.target.value}))}
                                required
                                style={inputStyle}
                            />
                        </div>
                        {/* Floor */}
                        <div>
                            <label style={labelStyle}>
                                {t.floor} <span style={{color: "#b22222"}}>*</span>
                            </label>
                            <input
                                name="floor-no-autofill"
                                type="text"
                                autoComplete="one-time-code"
                                data-lpignore="true"
                                data-1p-ignore="true"
                                value={createUser.street}
                                onChange={(e) => setCreateUser((prev) => ({...prev, street: e.target.value}))}
                                required
                                style={inputStyle}
                            />
                        </div>
                    </div>

                    {/* Password (optional) */}
                    <div style={{marginBottom: "8px"}}>
                        <label style={{...labelStyle, color: "#555", fontWeight: "600"}}>
                            {t.password}
                        </label>
                        <div style={{position: "relative"}}>
                            <input
                                name="password"
                                type={showPassword ? "text" : "password"}
                                value={createUser.password}
                                onChange={handleInputChange}
                                placeholder={t.passwordPlaceholder}
                                inputMode="numeric"
                                style={{...inputStyle, paddingLeft: "44px"}}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((p) => !p)}
                                style={{
                                    position: "absolute",
                                    left: "12px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#c9a84c",
                                    fontSize: "18px",
                                    padding: 0,
                                    lineHeight: 1,
                                }}
                            >
                                {showPassword ? "👁" : "🙈"}
                            </button>
                        </div>
                    </div>
                    <p style={{textAlign: "right", color: "#888", fontSize: "12px", marginBottom: "24px"}}>
                        {t.passwordHint}
                    </p>

                    {/* Confirm Button */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        style={{
                            width: "100%",
                            padding: "16px",
                            background: isLoading
                                ? "#e57373"
                                : "linear-gradient(135deg, #c0392b 0%, #922b21 100%)",
                            color: "#fff",
                            border: "none",
                            borderRadius: "10px",
                            fontSize: "20px",
                            fontWeight: "bold",
                            cursor: isLoading ? "not-allowed" : "pointer",
                            boxShadow: "0 3px 12px rgba(192,57,43,0.35)",
                            fontFamily: "Cairo, sans-serif",
                            marginBottom: "14px",
                        }}
                    >
                        {isLoading ? t.confirming : t.confirm}
                    </button>

                    {/* Cancel */}
                    <div style={{textAlign: "center"}}>
                        <Link
                            to="/Login"
                            style={{
                                color: "#555",
                                textDecoration: "underline",
                                fontSize: "16px",
                                fontFamily: "Cairo, sans-serif",
                            }}
                        >
                            {t.cancel}
                        </Link>
                    </div>
                </form>
            </div>

            {/* ── Governorate picker popup ── */}
            {isGovPopupOpen && (
                <div
                    style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"flex-end",justifyContent:"center"}}
                    onClick={() => { setIsGovPopupOpen(false); setGovSearchTerm(""); }}
                >
                    <div
                        style={{background:"#fff",width:"100%",maxWidth:"480px",borderRadius:"20px 20px 0 0",maxHeight:"80vh",display:"flex",flexDirection:"column"}}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"18px 20px 12px",borderBottom:"1px solid #eee",flexShrink:0}}>
                            <span style={{fontFamily:"Cairo,sans-serif",fontWeight:"bold",fontSize:"17px"}}>
                                {language === "ar" ? "اختار منطقة التوصيل" : "Select delivery area"}
                            </span>
                            <button onClick={() => { setIsGovPopupOpen(false); setGovSearchTerm(""); }} style={{fontSize:"22px",background:"none",border:"none",cursor:"pointer",color:"#888"}}>×</button>
                        </div>
                        <div style={{padding:"12px 16px",flexShrink:0}}>
                            <div style={{position:"relative"}}>
                                <span style={{position:"absolute",top:"50%",transform:"translateY(-50%)",right:"12px",color:"#aaa"}}>🔍</span>
                                <input
                                    autoFocus
                                    type="text"
                                    value={govSearchTerm}
                                    onChange={(e) => setGovSearchTerm(e.target.value)}
                                    placeholder={language === "ar" ? "ابحث..." : "Search..."}
                                    style={{width:"100%",padding:"10px 40px 10px 14px",borderRadius:"12px",border:"1px solid #ddd",fontFamily:"Cairo,sans-serif",fontSize:"14px",direction:"rtl",boxSizing:"border-box",outline:"none"}}
                                />
                            </div>
                        </div>
                        <div style={{overflowY:"auto",flex:1,padding:"0 8px 16px",overscrollBehavior:"contain"}}>
                            {activeGovernorates
                                .filter(gov => !govSearchTerm.trim() || gov.name?.toLowerCase().includes(govSearchTerm.trim().toLowerCase()))
                                .map(gov => (
                                    <button
                                        key={gov.id}
                                        type="button"
                                        onClick={() => {
                                            handleGovernorateChange(gov.id);
                                            setSelectedGovName(gov.name);
                                            setIsGovPopupOpen(false);
                                            setGovSearchTerm("");
                                        }}
                                        style={{width:"100%",textAlign:"right",padding:"13px 16px",borderRadius:"12px",border:"none",background: createUser.governorate === gov.id ? "#fff0ee" : "transparent",cursor:"pointer",fontFamily:"Cairo,sans-serif",fontSize:"15px",fontWeight: createUser.governorate === gov.id ? "bold" : "normal",color: createUser.governorate === gov.id ? "#c0392b" : "#222",display:"block",marginBottom:"2px"}}
                                    >
                                        {gov.name}
                                    </button>
                                ))
                            }
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Registration;
